import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WizardShell } from './components/WizardShell';
import { AddressStep } from './steps/AddressStep';
import { UnitStep, UnitSelection } from './steps/UnitStep';
import { UserDataStep } from './steps/UserDataStep';
import { DocumentStep } from './steps/DocumentStep';
import { CodeStep } from './steps/CodeStep';
import { SuccessStep } from './steps/SuccessStep';
import type { AddressLookupResult, RegistrationState, WizardStep } from './types';

const INITIAL_STATE: RegistrationState = {
  registrationId: null,
  branch: null,
  address: null,
  towers: [],
  selectedTowerId: null,
  selectedUnitId: null,
  documentRequired: false,
  email: null,
};

interface StepMeta {
  title: string;
  subtitle?: string;
}

/**
 * Wizard de auto-cadastro de clientes.
 *
 * Máquina de estados em memória (nada é persistido em localStorage por questões
 * de LGPD/segurança). Fluxo: endereço → unidade → dados → [documento] → código.
 */
export function RegistrationPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState<WizardStep>('address');
  const [state, setState] = useState<RegistrationState>(INITIAL_STATE);

  const stepMeta: Record<WizardStep, StepMeta> = {
    address: {
      title: t('views.registration.steps.address.title', 'Crie sua conta'),
      subtitle: t('views.registration.steps.address.subtitle', 'Informe o CEP e o número do seu endereço para localizarmos sua unidade.'),
    },
    unit: {
      title: t('views.registration.steps.unit.title', 'Confirme sua unidade'),
      subtitle: t('views.registration.steps.unit.subtitle', 'Selecione sua unidade ou comprove a titularidade da ligação.'),
    },
    user_data: {
      title: t('views.registration.steps.user_data.title', 'Seus dados'),
      subtitle: t('views.registration.steps.user_data.subtitle', 'Preencha seus dados de acesso.'),
    },
    document: {
      title: t('views.registration.steps.document.title', 'Comprovação'),
      subtitle: t('views.registration.steps.document.subtitle', 'Envie um documento que comprove sua titularidade.'),
    },
    code: {
      title: t('views.registration.steps.code.title', 'Confirmação'),
    },
    success: {
      title: t('views.registration.steps.success.title', 'Pronto!'),
    },
  };

  // Rola para o topo ao trocar de passo (melhor UX no mobile).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Passos visíveis no indicador de progresso (documento é condicional).
  const indicatorSteps = useMemo(() => {
    const base = [
      { key: 'address', label: t('views.registration.indicator.address', 'Endereço') },
      { key: 'unit', label: t('views.registration.indicator.unit', 'Unidade') },
      { key: 'user_data', label: t('views.registration.indicator.user_data', 'Seus dados') },
    ];
    if (state.documentRequired) base.push({ key: 'document', label: t('views.registration.indicator.document', 'Documento') });
    base.push({ key: 'code', label: t('views.registration.indicator.code', 'Confirmação') });
    return base;
  }, [state.documentRequired, t]);

  const currentIndex = Math.max(
    0,
    indicatorSteps.findIndex((s) => s.key === step),
  );

  // --- Handlers de transição -------------------------------------------------

  const handleAddressComplete = (result: AddressLookupResult) => {
    setState((prev) => ({
      ...prev,
      registrationId: result.registrationId,
      branch: result.branch,
      address: result.address,
      towers: result.towers || [],
      documentRequired: false,
    }));
    setStep('unit');
  };

  const handleUnitComplete = (selection: UnitSelection) => {
    setState((prev) => ({
      ...prev,
      documentRequired: selection.documentRequired,
      selectedTowerId: selection.commercialServiceId ?? null,
      selectedUnitId: selection.unitId ?? null,
    }));
    setStep('user_data');
  };

  const handleUserDataComplete = (email: string) => {
    setState((prev) => ({ ...prev, email }));
    setStep(state.documentRequired ? 'document' : 'code');
  };

  const handleDocumentComplete = () => setStep('code');
  const handleCodeComplete = () => setStep('success');

  const meta = stepMeta[step];

  return (
    <WizardShell
      steps={indicatorSteps}
      currentIndex={currentIndex}
      title={meta.title}
      subtitle={meta.subtitle}
      hideStepper={step === 'success'}
    >
      {step === 'address' && (
        <AddressStep
          initialZip={state.address?.zipCode ?? ''}
          initialNumber={state.address?.number ?? ''}
          onComplete={handleAddressComplete}
        />
      )}

      {step === 'unit' && state.branch && state.registrationId && (
        <UnitStep
          registrationId={state.registrationId}
          branch={state.branch}
          address={state.address}
          towers={state.towers}
          onComplete={handleUnitComplete}
          onBack={() => setStep('address')}
        />
      )}

      {step === 'user_data' && state.registrationId && (
        <UserDataStep
          registrationId={state.registrationId}
          onComplete={handleUserDataComplete}
          onBack={() => setStep('unit')}
        />
      )}

      {step === 'document' && state.registrationId && (
        <DocumentStep
          registrationId={state.registrationId}
          onComplete={handleDocumentComplete}
          onBack={() => setStep('user_data')}
        />
      )}

      {step === 'code' && state.registrationId && state.email && (
        <CodeStep
          email={state.email}
          onComplete={handleCodeComplete}
          onBack={() => setStep(state.documentRequired ? 'document' : 'user_data')}
        />
      )}

      {step === 'success' && <SuccessStep />}
    </WizardShell>
  );
}

export default RegistrationPage;
